import { ggVars, toUpperSnakeCase } from 'constants/ggVars';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import ReportPositiveNegativeDriversQuery from '@graphql/queries/ReportPositiveNegativeDriversQuery';
import { Box } from '@mui/material';
import useCustomProductNames from 'hooks/useCustomProductNames';
import { groupBy, keys } from 'lodash';
import { Data, LabelKeyObject } from 'react-csv/lib/core';

import { getProductName } from '../../../../components/Report/utils';
import DataDownloadTile from '../DataDownloadTile';
import { TileType } from '../DataDownloadTile/DataDownloadTile';

interface Props {
  projectId: number;
  reportId: string;
}

const toCorrelateOfQuality = (nodeColor: string): number => {
  if (nodeColor === 'green') return 1;
  if (nodeColor === 'red') return 2;
  return 0;
};

const AllOFPsCorrelatesOfQualityDownload: React.FC<Props> = (props) => {
  const { projectId, reportId } = props;

  const productNames = useCustomProductNames({ projectId: Number(projectId) });

  const { loading, error, data } =
    useQuery<reports.ReportPositiveNegativeDriversResponse>(
      ReportPositiveNegativeDriversQuery,
      {
        variables: {
          reportID: reportId,
        },
      },
    );

  const getCsvHeader: () => LabelKeyObject[] = () => {
    return [{ label: 'Product', key: 'product' }].concat(ggVars).map((gv) => ({
      label: gv.label,
      key: gv.key,
    }));
  };

  const getCsvRows: (data: reports.ReportPositiveNegativeDriversResponse) => Data = (
    data: reports.ReportPositiveNegativeDriversResponse,
  ) => {
    const products = groupBy(
      data.allRpFlavorStructures.nodes.filter((n) => !!n.product && !!n.label),
      (n) => n.product.id,
    );

    const rows = [];
    keys(products).forEach((p) => {
      const row = [];

      const productNode = products[p].find(Boolean);

      getCsvHeader().forEach((h) => {
        if (h.key === 'product') {
          row.push(
            getProductName({
              productNames,
              productId: productNode.product.id,
              version: null,
            }),
          );
        } else {
          const item = products[p].find(
            (g) => toUpperSnakeCase(g.label) === toUpperSnakeCase(h.key),
          );
          row.push(toCorrelateOfQuality(item?.nodeColor));
        }
      });

      rows.push(row);
    });
    return rows;
  };

  if (loading || error || getCsvRows(data).length === 0) {
    return <Box />;
  }

  return (
    <DataDownloadTile
      tileType={TileType.CSV}
      headers={getCsvHeader().map((h) => h.label)}
      csvDataRows={getCsvRows(data)}
      fileName="AllOfpsCorrelatesOfQuality.csv"
      tileName="All OFPs Correlates of Quality"
    />
  );
};

export default AllOFPsCorrelatesOfQualityDownload;
